import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const services = [
  {
    id: 1,
    title: 'Living Spaces',
    description: 'Balanced, warm, and designed for everyday living.',
    image: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?w=600&q=80',
  },
  {
    id: 2,
    title: 'Wardrobes & Storage',
    description: 'Functional systems with refined finishes and thoughtful detailing.',
    image: 'https://images.pexels.com/photos/6508343/pexels-photo-6508343.jpeg?w=600&q=80',
  },
  {
    id: 3,
    title: 'Kitchens',
    description: 'Minimal, efficient, and built around usage—not just aesthetics.',
    image: 'https://customer-assets.emergentagent.com/job_design-intent/artifacts/3askcg73_kichen001.jpg',
  },
];

export const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section
      id="services"
      ref={ref}
      data-testid="services-section"
      className="section-padding bg-[#F5F0E8]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="mb-16"
        >
          <span className="overline text-[#B5703A] block mb-4">Our Expertise</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#2A2A2A] tracking-tight">
            Designed Around the<br />
            Way You Live
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.15 }}
              className="group"
              data-testid={`service-card-${service.id}`}
            >
              <div className="image-hover-scale aspect-[4/3] bg-[#D9CFC0] mb-6">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-xl lg:text-2xl font-light text-[#2A2A2A] mb-3">
                {service.title}
              </h3>
              <p className="text-[#3D3D3D] font-light text-sm leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-16"
        >
          <Link
            to="/portfolio"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="services-cta"
          >
            View Portfolio
            <ArrowRight size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
